import { lazy, Suspense, useEffect } from 'react';
import { Landing } from './pages/Landing';
// Marketing pages are static imports (not lazy) so they prerender + hydrate
// without a Suspense boundary mismatch, same as Landing.
import { PricingPage } from './pages/marketing/PricingPage';
import { WhatsAppFeaturePage } from './pages/marketing/WhatsAppFeaturePage';
import { FeesFeaturePage } from './pages/marketing/FeesFeaturePage';
import { AccountingFeaturePage } from './pages/marketing/AccountingFeaturePage';
import { ReportsFeaturePage } from './pages/marketing/ReportsFeaturePage';
import { EcocashFeaturePage } from './pages/marketing/EcocashFeaturePage';
import { FaqPage } from './pages/marketing/FaqPage';
import { ZimsecExamsPage } from './pages/marketing/ZimsecExamsPage';
import { SecondarySchoolsPage } from './pages/marketing/SecondarySchoolsPage';
import { AboutPage } from './pages/marketing/AboutPage';
import { ContactPage } from './pages/marketing/ContactPage';
import { TermsAndConditions } from './pages/TermsAndConditions';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { NotFoundPage } from './pages/NotFoundPage';
import { ScrollToTop } from './components/common/ScrollToTop';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import { HelmetProvider } from 'react-helmet-async';
const AppLayout = lazy(() => import('./components/common/Layout').then(m => ({ default: m.AppLayout })));
import { InstallPromptModal } from './components/common/InstallPromptModal';
import ProtectedRoute from './components/common/ProtectedRoute/ProtectedRoute';
import { theme } from './assets/styles/theme';
import { useAuthStore } from './store/authStore';
import { getDefaultRouteForRole } from './utils/permissions';

// Lazy load page components for code splitting.
// Landing is imported statically (not lazy) so it is prerendered + hydrated
// without a Suspense boundary mismatch, and its CSS ships in the main bundle.
const Login = lazy(() => import('./pages/Login').then(module => ({ default: module.Login })));
const Register = lazy(() => import('./pages/Register').then(module => ({ default: module.Register })));
const GetStarted = lazy(() => import('./pages/GetStarted').then(module => ({ default: module.GetStarted })));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail').then(module => ({ default: module.VerifyEmail })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(module => ({ default: module.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(module => ({ default: module.ResetPassword })));
const ResetPasswordCode = lazy(() => import('./pages/ResetPasswordCode').then(module => ({ default: module.ResetPasswordCode })));
const Suspended = lazy(() => import('./pages/Suspended').then(module => ({ default: module.Suspended })));
const Deactivated = lazy(() => import('./pages/Deactivated').then(module => ({ default: module.Deactivated })));
const TrialEnded = lazy(() => import('./pages/TrialEnded').then(module => ({ default: module.TrialEnded })));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const AdminAnnouncements = lazy(() => import('./pages/AdminAnnouncements'));
const PlatformPayments = lazy(() => import('./pages/PlatformPayments'));
const PlatformSupport = lazy(() => import('./pages/PlatformSupport'));
const Classes = lazy(() => import('./pages/Classes'));
const Students = lazy(() => import('./pages/Students').then(module => ({ default: module.Students })));
const Payments = lazy(() => import('./pages/Payments').then(module => ({ default: module.Payments })));
const Attendance = lazy(() => import('./pages/Attendance').then(module => ({ default: module.Attendance })));
const Expenses = lazy(() => import('./pages/Expenses').then(module => ({ default: module.Expenses })));
const ExpenseCategories = lazy(() => import('./pages/ExpenseCategories'));
const Users = lazy(() => import('./pages/Users').then(module => ({ default: module.Users })));
const Reports = lazy(() => import('./pages/Reports').then(module => ({ default: module.Reports })));
const Schools = lazy(() => import('./pages/Schools').then(module => ({ default: module.Schools })));
const BlogPosts = lazy(() => import('./pages/BlogPosts').then(module => ({ default: module.BlogPosts })));
const NewsPage = lazy(() => import('./pages/NewsPage').then(module => ({ default: module.NewsPage })));
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage').then(module => ({ default: module.NewsDetailPage })));
const SystemDiagnostics = lazy(() => import('./pages/SystemDiagnostics'));
const AcademicYears = lazy(() => import('./pages/AcademicYears'));
const Billing = lazy(() => import('./pages/Billing').then(module => ({ default: module.Billing })));
const Communication = lazy(() => import('./pages/Communication'));
const Teachers = lazy(() => import('./pages/Teachers').then(module => ({ default: module.Teachers })));
const SubjectsPage = lazy(() => import('./pages/SubjectsPage').then(module => ({ default: module.SubjectsPage })));
const Assessments = lazy(() => import('./pages/Assessments').then(module => ({ default: module.Assessments })));
const Results = lazy(() => import('./pages/Results').then(module => ({ default: module.Results })));
const GradingScales = lazy(() => import('./pages/GradingScales').then(module => ({ default: module.GradingScales })));
const Promotions = lazy(() => import('./pages/Promotions').then(module => ({ default: module.Promotions })));
const AcademicSettings = lazy(() => import('./pages/AcademicSettings').then(module => ({ default: module.AcademicSettings })));


// Add-Ons Page
const AddOns = lazy(() => import('./pages/AddOns').then(module => ({ default: module.AddOns })));

// Accounting Pages
const AccountingOverviewPage = lazy(() => import('./pages/accounting/AccountingOverviewPage'));
const ChartOfAccountsPage = lazy(() => import('./pages/accounting/ChartOfAccountsPage'));
const JournalEntriesPage = lazy(() => import('./pages/accounting/JournalEntriesPage'));
const GeneralLedgerPage = lazy(() => import('./pages/accounting/GeneralLedgerPage'));
const BudgetsPage = lazy(() => import('./pages/accounting/BudgetsPage'));
const BudgetDetailPage = lazy(() => import('./pages/accounting/BudgetDetailPage'));
const PayrollPage = lazy(() => import('./pages/accounting/PayrollPage'));
const CreditorsPage = lazy(() => import('./pages/accounting/CreditorsPage'));
const AccountingReportsPage = lazy(() => import('./pages/accounting/AccountingReportsPage'));
const RequisitionsPage = lazy(() => import('./pages/accounting/RequisitionsPage'));
const FixedAssetsPage = lazy(() => import('./pages/accounting/FixedAssetsPage'));
const StudentInvoicesPage = lazy(() => import('./pages/accounting/StudentInvoicesPage'));

// Meeting Recordings Page
const Meetings = lazy(() => import('./pages/Meetings'));

// Fleet Management Pages
const FleetDashboard = lazy(() => import('./pages/fleet/FleetDashboard'));
const FleetVehicles = lazy(() => import('./pages/fleet/FleetVehicles'));
const FleetDrivers = lazy(() => import('./pages/fleet/FleetDrivers'));
const FleetRoutes = lazy(() => import('./pages/fleet/FleetRoutes'));
const FleetSchedules = lazy(() => import('./pages/fleet/FleetSchedules'));
const FleetChecklists = lazy(() => import('./pages/fleet/FleetChecklists'));
const FleetRequisitions = lazy(() => import('./pages/fleet/FleetRequisitions'));
const FleetHireOut = lazy(() => import('./pages/fleet/FleetHireOut'));
const FleetFeedback = lazy(() => import('./pages/fleet/FleetFeedback'));

// Inventory Management Pages
const InventoryOverviewPage = lazy(() => import('./pages/inventory/InventoryOverviewPage'));
const InventoryCategoriesPage = lazy(() => import('./pages/inventory/InventoryCategoriesPage'));
const InventoryItemsPage = lazy(() => import('./pages/inventory/InventoryItemsPage'));
const CollectionQueuePage = lazy(() => import('./pages/inventory/CollectionQueuePage'));

// Driver Portal Pages
const DriverDashboard = lazy(() => import('./pages/driver/DriverDashboard'));
const DriverSchedules = lazy(() => import('./pages/driver/DriverSchedules'));
const DriverChecklist = lazy(() => import('./pages/driver/DriverChecklist'));
const DriverRequisitions = lazy(() => import('./pages/driver/DriverRequisitions'));

// Charges Management Pages
const ChargeItems = lazy(() => import('./pages/ChargeItems').then(module => ({ default: module.ChargeItems })));

// Support Tickets (parent/student messaging)
const Tickets = lazy(() => import('./pages/Tickets').then(module => ({ default: module.Tickets })));

// Teacher Portal Pages
const TeacherDashboard = lazy(() => import('./pages/teacher').then(module => ({ default: module.TeacherDashboard })));
const TeacherClasses = lazy(() => import('./pages/teacher').then(module => ({ default: module.TeacherClasses })));
const TeacherSubjects = lazy(() => import('./pages/teacher').then(module => ({ default: module.TeacherSubjects })));
const TeacherStudents = lazy(() => import('./pages/teacher').then(module => ({ default: module.TeacherStudents })));
const TeacherAssessments = lazy(() => import('./pages/teacher').then(module => ({ default: module.TeacherAssessments })));
const TeacherAttendance = lazy(() => import('./pages/teacher').then(module => ({ default: module.TeacherAttendance })));
const TeacherAssessmentForm = lazy(() => import('./pages/teacher').then(module => ({ default: module.TeacherAssessmentForm })));
const TeacherMarksEntry = lazy(() => import('./pages/teacher').then(module => ({ default: module.TeacherMarksEntry })));
const EcdOverview = lazy(() => import('./pages/teacher').then(module => ({ default: module.EcdOverview })));
const EcdClassView = lazy(() => import('./pages/teacher').then(module => ({ default: module.EcdClassView })));
const EcdStudentDetail = lazy(() => import('./pages/teacher').then(module => ({ default: module.EcdStudentDetail })));

// Admin ECD Pages
const EcdAdminOverview = lazy(() => import('./pages/admin/EcdAdminOverview').then(module => ({ default: module.EcdAdminOverview })));

// Guardian Portal Pages
const GuardianDashboard = lazy(() => import('./pages/guardian/GuardianDashboard'));
const ChildFinancials = lazy(() => import('./pages/guardian/ChildFinancials'));
const ChildAcademics = lazy(() => import('./pages/guardian/ChildAcademics'));
const ChildAttendance = lazy(() => import('./pages/guardian/ChildAttendance'));
const ChildTeachers = lazy(() => import('./pages/guardian/ChildTeachers'));
const GuardianFleetFeedback = lazy(() => import('./pages/guardian/GuardianFleetFeedback'));
const GuardianChildEcdProgress = lazy(() => import('./pages/guardian/GuardianChildEcdProgress'));
const GuardianMessages = lazy(() => import('./pages/guardian/GuardianMessages'));

// Student Portal Pages
const StudentDashboard = lazy(() => import('./pages/student/StudentDashboard'));
const StudentAcademics = lazy(() => import('./pages/student/StudentAcademics'));
const StudentAttendance = lazy(() => import('./pages/student/StudentAttendance'));
const StudentPortalSubjects = lazy(() => import('./pages/student/StudentSubjects'));
const StudentFinancials = lazy(() => import('./pages/student/StudentFinancials'));
const StudentProfile = lazy(() => import('./pages/student/StudentProfile'));
const StudentMessages = lazy(() => import('./pages/student/StudentMessages'));

// Public Enrollment Page
const PublicEnrollmentForm = lazy(() => import('./pages/public/PublicEnrollmentForm').then(module => ({ default: module.PublicEnrollmentForm })));


/**
 * RoleBasedRedirect Component
 * Redirects users to their default route based on role
 * Guards go to /attendance, all others go to /dashboard
 */
const RoleBasedRedirect = () => {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // If not authenticated, redirect to landing page
  if (!isAuthenticated) {
    return <Navigate to="/landing" replace />;
  }

  const defaultRoute = user ? getDefaultRouteForRole(user.role) : '/dashboard';
  return <Navigate to={defaultRoute} replace />;
};

/**
 * Main App component
 * Sets up routing, authentication, and theme configuration
 * Implements role-based access control for all routes
 */
function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <HelmetProvider>
      <ConfigProvider theme={theme}>
        <AntApp>
          <InstallPromptModal />
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ScrollToTop />
            <Suspense fallback={null}>
            <Routes>
              {/* Landing is a static import (prerendered + hydrated), so it
                  never suspends - no Suspense/skeleton needed here. */}
              <Route path="/" element={<Landing />} />
              <Route path="/landing" element={<Landing />} />

              {/* Prerendered marketing pages - keep in sync with
                  entry-server.tsx and src/prerender-routes.ts */}
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/features/whatsapp" element={<WhatsAppFeaturePage />} />
              <Route path="/features/fees" element={<FeesFeaturePage />} />
              <Route path="/features/accounting" element={<AccountingFeaturePage />} />
              <Route path="/features/reports" element={<ReportsFeaturePage />} />
              <Route path="/features/ecocash-payments" element={<EcocashFeaturePage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/features/zimsec-exams" element={<ZimsecExamsPage />} />
              <Route path="/secondary-schools" element={<SecondarySchoolsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />

              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/get-started" element={<GetStarted />} />
              <Route path="/register" element={<Register />} />
              <Route path="/signup" element={<Register />} /> {/* Redirect old signup route to register */}
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/reset-password-code" element={<ResetPasswordCode />} />
              <Route path="/suspended" element={<Suspended />} />
              <Route path="/deactivated" element={<Deactivated />} />
              <Route path="/trial-ended" element={<TrialEnded />} />
              
              {/* Public Enrollment Form - No authentication required */}
              <Route path="/enroll/:token" element={<PublicEnrollmentForm />} />

              {/* Public News/Blog - No authentication required */}
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<NewsDetailPage />} />

              {/* Protected routes wrapped in AppLayout */}
              <Route element={<AppLayout />}>

                {/* SuperAdmin Dashboard - accessible to SuperAdmin only */}
                <Route
                  path="/superadmin-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <SuperAdminDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Dashboard - accessible to SchoolAdmin, Principal */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer']}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Levels - redirects to Classes Levels tab */}
                <Route path="/levels" element={<Navigate to="/classes?tab=levels" replace />} />

                {/* Classes - SchoolAdmin, Principal */}
                <Route
                  path="/classes"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <Classes />
                    </ProtectedRoute>
                  }
                />

                {/* Students - SchoolAdmin, Principal, Receptionist */}
                <Route
                  path="/students"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'Receptionist']}>
                      <Students />
                    </ProtectedRoute>
                  }
                />

                {/* Legacy routes - redirect to Students tabs */}
                <Route path="/students/inactive" element={<Navigate to="/students" replace />} />
                <Route path="/students/graduated" element={<Navigate to="/students?tab=alumni" replace />} />

                {/* Support Tickets / Messages - SchoolAdmin, Principal, FinanceOfficer */}
                <Route
                  path="/tickets"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer']}>
                      <Tickets />
                    </ProtectedRoute>
                  }
                />

                {/* Payments - SchoolAdmin, Principal, FinanceOfficer, Receptionist */}
                <Route
                  path="/payments"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer', 'AccountsClerk', 'Receptionist']}>
                      <Payments />
                    </ProtectedRoute>
                  }
                />

                {/* Attendance - SchoolAdmin, Guard, Receptionist */}
                <Route
                  path="/attendance"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Guard', 'Receptionist']}>
                      <Attendance />
                    </ProtectedRoute>
                  }
                />

                {/* Expenses - SchoolAdmin, Principal, FinanceOfficer, Receptionist */}
                <Route
                  path="/expenses"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer', 'AccountsClerk', 'Receptionist']}>
                      <Expenses />
                    </ProtectedRoute>
                  }
                />

                {/* Expense Categories - FinanceOfficer, Receptionist */}
                <Route
                  path="/expense-categories"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'Receptionist']}>
                      <ExpenseCategories />
                    </ProtectedRoute>
                  }
                />

                {/* Users - SuperAdmin, SchoolAdmin */}
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin', 'SchoolAdmin']}>
                      <Users />
                    </ProtectedRoute>
                  }
                />

                {/* Reports - SchoolAdmin, Principal, FinanceOfficer */}
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer']}>
                      <Reports />
                    </ProtectedRoute>
                  }
                />

                {/* Schools - SuperAdmin only */}
                <Route
                  path="/schools"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <Schools />
                    </ProtectedRoute>
                  }
                />

                {/* News & Blog Management - SuperAdmin only */}
                <Route
                  path="/blog-posts"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <BlogPosts />
                    </ProtectedRoute>
                  }
                />

                {/* Announcements - SuperAdmin only */}
                <Route
                  path="/admin-announcements"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <AdminAnnouncements />
                    </ProtectedRoute>
                  }
                />

                {/* Platform Payments - SuperAdmin only */}
                <Route
                  path="/platform-payments"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <PlatformPayments />
                    </ProtectedRoute>
                  }
                />

                {/* Support Contacts - SuperAdmin only */}
                <Route
                  path="/platform-support"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <PlatformSupport />
                    </ProtectedRoute>
                  }
                />

                {/* System Diagnostics - SuperAdmin only */}
                <Route
                  path="/system-diagnostics"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <SystemDiagnostics />
                    </ProtectedRoute>
                  }
                />

                {/* Academic Years - SuperAdmin only */}
                <Route
                  path="/academic-years"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <AcademicYears />
                    </ProtectedRoute>
                  }
                />

                {/* Billing - SuperAdmin only */}
                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute allowedRoles={['SuperAdmin']}>
                      <Billing />
                    </ProtectedRoute>
                  }
                />

                {/* Add-Ons - SchoolAdmin, Principal */}
                <Route
                  path="/add-ons"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <AddOns />
                    </ProtectedRoute>
                  }
                />

                {/* Meeting Recordings - SchoolAdmin, Principal */}
                <Route
                  path="/meetings"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <Meetings />
                    </ProtectedRoute>
                  }
                />

                {/* Communication - SchoolAdmin, Principal, SuperAdmin */}
                <Route
                  path="/communication/*"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'SuperAdmin']}>
                      <Communication />
                    </ProtectedRoute>
                  }
                />

                {/* Teachers - SchoolAdmin, Principal */}
                <Route
                  path="/teachers"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <Teachers />
                    </ProtectedRoute>
                  }
                />

                {/* Guardians - redirects to Students Guardians tab */}
                <Route path="/guardians" element={<Navigate to="/students?tab=guardians" replace />} />

                {/* Subjects - SchoolAdmin, Principal */}
                <Route
                  path="/subjects"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <SubjectsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Assessments - SchoolAdmin, Principal, Teacher */}
                <Route
                  path="/assessments"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'Teacher']}>
                      <Assessments />
                    </ProtectedRoute>
                  }
                />

                {/* Results - SchoolAdmin, Principal, Teacher */}
                <Route
                  path="/results"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'Teacher']}>
                      <Results />
                    </ProtectedRoute>
                  }
                />

                {/* Grading Scales - SchoolAdmin, Principal */}
                <Route
                  path="/grading-scales"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <GradingScales />
                    </ProtectedRoute>
                  }
                />

                {/* Promotions - SchoolAdmin, Principal */}
                <Route
                  path="/promotions"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <Promotions />
                    </ProtectedRoute>
                  }
                />

                {/* Academic Settings - SchoolAdmin, Principal */}
                <Route
                  path="/academic-settings"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <AcademicSettings />
                    </ProtectedRoute>
                  }
                />


                {/* Charges Management Routes - SchoolAdmin, Principal, FinanceOfficer */}
                <Route
                  path="/charges/items"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer']}>
                      <ChargeItems />
                    </ProtectedRoute>
                  }
                />

                {/* Teacher Portal Routes - Teacher only */}
                <Route
                  path="/teacher/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/classes"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherClasses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/subjects"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherSubjects />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/students"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherStudents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/assessments"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherAssessments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/attendance"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherAttendance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/assessments/create"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherAssessmentForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/assessments/edit/:id"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherAssessmentForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/marks/:assessmentId/:slug?"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <TeacherMarksEntry />
                    </ProtectedRoute>
                  }
                />

                {/* ECD Routes - Teacher only */}
                <Route
                  path="/teacher/ecd"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <EcdOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/ecd/class/:classId"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <EcdClassView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/ecd/student/:studentId"
                  element={
                    <ProtectedRoute allowedRoles={['Teacher']}>
                      <EcdStudentDetail />
                    </ProtectedRoute>
                  }
                />

                {/* Admin ECD Routes - SchoolAdmin, Principal */}
                <Route
                  path="/admin/ecd"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <EcdAdminOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/ecd/student/:studentId"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal']}>
                      <EcdStudentDetail readOnly={true} />
                    </ProtectedRoute>
                  }
                />

                {/* Guardian Portal Routes - Parent only */}
                <Route
                  path="/guardian/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['Parent']}>
                      <GuardianDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/guardian/children/:id/financials"
                  element={
                    <ProtectedRoute allowedRoles={['Parent']}>
                      <ChildFinancials />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/guardian/children/:id/academics"
                  element={
                    <ProtectedRoute allowedRoles={['Parent']}>
                      <ChildAcademics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/guardian/children/:id/attendance"
                  element={
                    <ProtectedRoute allowedRoles={['Parent']}>
                      <ChildAttendance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/guardian/children/:id/teachers"
                  element={
                    <ProtectedRoute allowedRoles={['Parent']}>
                      <ChildTeachers />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guardian/children/:id/ecd"
                  element={
                    <ProtectedRoute allowedRoles={['Parent']}>
                      <GuardianChildEcdProgress />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guardian/fleet-feedback"
                  element={
                    <ProtectedRoute allowedRoles={['Parent']} requiresFeature="fleet-management">
                      <GuardianFleetFeedback />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/guardian/messages"
                  element={
                    <ProtectedRoute allowedRoles={['Parent']}>
                      <GuardianMessages />
                    </ProtectedRoute>
                  }
                />

                {/* Student Portal Routes - Student only */}
                <Route
                  path="/student/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['Student']}>
                      <StudentDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/academics"
                  element={
                    <ProtectedRoute allowedRoles={['Student']}>
                      <StudentAcademics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/attendance"
                  element={
                    <ProtectedRoute allowedRoles={['Student']}>
                      <StudentAttendance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/subjects"
                  element={
                    <ProtectedRoute allowedRoles={['Student']}>
                      <StudentPortalSubjects />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/financials"
                  element={
                    <ProtectedRoute allowedRoles={['Student']}>
                      <StudentFinancials />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/profile"
                  element={
                    <ProtectedRoute allowedRoles={['Student']}>
                      <StudentProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/messages"
                  element={
                    <ProtectedRoute allowedRoles={['Student']}>
                      <StudentMessages />
                    </ProtectedRoute>
                  }
                />

                {/* Advanced Accounting Routes */}
                <Route
                  path="/accounting"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <AccountingOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/chart-of-accounts"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <ChartOfAccountsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/journal-entries"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <JournalEntriesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/general-ledger"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <GeneralLedgerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/budgets"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <BudgetsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/budgets/:id"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <BudgetDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/payroll"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <PayrollPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/creditors"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <CreditorsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/reports"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <AccountingReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/requisitions"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <RequisitionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/fixed-assets"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <FixedAssetsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting/student-invoices"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer']}>
                      <StudentInvoicesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Fleet Management Routes - FleetAdmin (SchoolAdmin, FinanceOfficer, FleetManager) */}
                <Route
                  path="/fleet/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleet/vehicles"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetVehicles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleet/drivers"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetDrivers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleet/routes"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetRoutes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleet/schedules"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetSchedules />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleet/checklists"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetChecklists />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleet/requisitions"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetRequisitions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleet/hire-out"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetHireOut />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fleet/feedback"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'FinanceOfficer', 'FleetManager']} requiresFeature="fleet-management">
                      <FleetFeedback />
                    </ProtectedRoute>
                  }
                />

                {/* Inventory Management Routes */}
                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer']} requiresFeature="inventory-management">
                      <InventoryOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/categories"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer']} requiresFeature="inventory-management">
                      <InventoryCategoriesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/items"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer', 'Receptionist']} requiresFeature="inventory-management">
                      <InventoryItemsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/collections"
                  element={
                    <ProtectedRoute allowedRoles={['SchoolAdmin', 'Principal', 'FinanceOfficer', 'Receptionist']} requiresFeature="inventory-management">
                      <CollectionQueuePage />
                    </ProtectedRoute>
                  }
                />

                {/* Driver Portal Routes - Driver and FleetManager */}
                <Route
                  path="/driver/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['Driver', 'FleetManager']}>
                      <DriverDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/driver/schedules"
                  element={
                    <ProtectedRoute allowedRoles={['Driver', 'FleetManager']}>
                      <DriverSchedules />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/driver/checklist"
                  element={
                    <ProtectedRoute allowedRoles={['Driver', 'FleetManager']}>
                      <DriverChecklist />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/driver/requisitions"
                  element={
                    <ProtectedRoute allowedRoles={['Driver', 'FleetManager']}>
                      <DriverRequisitions />
                    </ProtectedRoute>
                  }
                />

                {/* Links sent outside the app with no page of their own (Pesepay's
                    return URL, invoice emails) land on the user's home screen. */}
                <Route
                  path="/payment-result"
                  element={
                    <ProtectedRoute>
                      <RoleBasedRedirect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/invoices/:invoiceId"
                  element={
                    <ProtectedRoute>
                      <RoleBasedRedirect />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AntApp>
    </ConfigProvider>
    </HelmetProvider>
  );
}

export default App;
